import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import PageHeader from '../../components/PageHeader';
import { supabase } from '../../config/supabase';

const SocialPublishPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // État pour les communes avec include=true
  const [communes, setCommunes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // État pour les configurations réseaux sociaux
  const [socialConfigs, setSocialConfigs] = useState({});

  // État pour les jobs en cours
  const [publishingJobs, setPublishingJobs] = useState({});

  // État pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 20;

  // État pour les statistiques
  const [stats, setStats] = useState({
    totalActive: 0,
    totalPublished: 0,
    totalFailed: 0,
    totalPending: 0
  });

  // Récupérer les communes include=true
  const fetchCommunes = useCallback(async () => {
    setLoading(true);
    try {
      // Récupérer toutes les communes incluses et dédupliquer par code INSEE
      const { data, error: fetchError } = await supabase
        .from('communes')
        .select('code_commune_insee, nom_de_la_commune')
        .eq('include', true)
        .order('nom_de_la_commune', { ascending: true });

      if (fetchError) throw fetchError;

      // Dédupliquer par code INSEE
      const uniqueMap = new Map();
      data?.forEach(c => {
        if (!uniqueMap.has(c.code_commune_insee)) {
          uniqueMap.set(c.code_commune_insee, c);
        }
      });
      const allCommunes = Array.from(uniqueMap.values());
      setTotalCount(allCommunes.length);

      // Paginer côté client
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      setCommunes(allCommunes.slice(offset, offset + ITEMS_PER_PAGE));
    } catch (err) {
      console.error('Error fetching communes:', err);
      setError(t('socialPublish.loadError'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, t]);

  // Récupérer les configurations réseaux sociaux
  const fetchSocialConfigs = useCallback(async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('commune_social_networks')
        .select('*');

      if (fetchError) throw fetchError;

      // Indexer par code_commune_insee + network
      const configMap = {};
      data?.forEach(config => {
        const key = `${config.code_commune_insee}_${config.network}`;
        configMap[key] = config;
      });
      setSocialConfigs(configMap);
    } catch (err) {
      console.error('Error fetching social configs:', err);
    }
  }, []);

  // Récupérer les statistiques
  const fetchStats = useCallback(async () => {
    try {
      const { count: activeCount } = await supabase
        .from('commune_social_networks')
        .select('*', { count: 'exact', head: true })
        .eq('active', true);

      const { count: publishedCount } = await supabase
        .from('social_publish_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'published');

      const { count: failedCount } = await supabase
        .from('social_publish_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'failed');

      const { count: pendingCount } = await supabase
        .from('social_publish_jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      setStats({
        totalActive: activeCount || 0,
        totalPublished: publishedCount || 0,
        totalFailed: failedCount || 0,
        totalPending: pendingCount || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchCommunes();
      fetchSocialConfigs();
      fetchStats();
    }
  }, [isAdmin, fetchCommunes, fetchSocialConfigs, fetchStats]);

  // Activer/désactiver un réseau social pour une commune
  const toggleNetwork = async (codeInsee, network, currentlyActive) => {
    const key = `${codeInsee}_${network}`;
    const existingConfig = socialConfigs[key];

    try {
      if (existingConfig) {
        // Mettre à jour
        const { error: updateError } = await supabase
          .from('commune_social_networks')
          .update({
            active: !currentlyActive,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingConfig.id);

        if (updateError) throw updateError;
      } else {
        // Créer une nouvelle entrée
        const { error: insertError } = await supabase
          .from('commune_social_networks')
          .insert({
            code_commune_insee: codeInsee,
            network: network,
            active: true
          });

        if (insertError) throw insertError;
      }

      // Rafraîchir les configs
      await fetchSocialConfigs();
      await fetchStats();
    } catch (err) {
      console.error('Error toggling network:', err);
      setError(t('socialPublish.toggleError'));
    }
  };

  // Lancer une publication manuelle pour une commune
  const triggerPublish = async (codeInsee, network) => {
    const jobKey = `${codeInsee}_${network}`;
    setPublishingJobs(prev => ({ ...prev, [jobKey]: 'loading' }));

    try {
      // Chercher un événement éligible directement par code INSEE
      const today = new Date().toISOString().split('T')[0];
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + 3);
      const targetDateStr = targetDate.toISOString().split('T')[0];

      // D'abord les événements dans 3 jours
      let { data: events } = await supabase
        .from('events')
        .select('id')
        .eq('status', 'published')
        .eq('start_date', targetDateStr)
        .eq('code_commune_insee', codeInsee)
        .order('boost', { ascending: false, nullsFirst: false })
        .order('is_paid', { ascending: true, nullsFirst: false })
        .order('is_recurring', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false })
        .limit(1);

      if (!events || events.length === 0) {
        // Pas d'événement dans 3 jours → chercher le prochain événement disponible
        const { data: nextEvents } = await supabase
          .from('events')
          .select('id')
          .eq('status', 'published')
          .gte('start_date', today)
          .eq('code_commune_insee', codeInsee)
          .order('boost', { ascending: false, nullsFirst: false })
          .order('is_paid', { ascending: true, nullsFirst: false })
          .order('start_date', { ascending: true })
          .limit(1);

        if (!nextEvents || nextEvents.length === 0) {
          setPublishingJobs(prev => ({ ...prev, [jobKey]: 'no_event' }));
          setError(t('socialPublish.noEventsFound'));
          setTimeout(() => setPublishingJobs(prev => ({ ...prev, [jobKey]: null })), 3000);
          return;
        }

        events = nextEvents;
      }

      // Créer le job
      const { data: job, error: jobError } = await supabase
        .from('social_publish_jobs')
        .insert({
          code_commune_insee: codeInsee,
          network: network,
          event_id: events[0].id,
          status: 'pending',
          scheduled_at: new Date().toISOString()
        })
        .select()
        .single();

      if (jobError) throw jobError;

      // Appeler la Edge Function
      const { data: { session } } = await supabase.auth.getSession();
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/publish-social`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({ job_id: job.id })
      });

      const result = await response.json();

      if (result.success) {
        setPublishingJobs(prev => ({ ...prev, [jobKey]: 'success' }));
      } else {
        setPublishingJobs(prev => ({ ...prev, [jobKey]: 'error' }));
        setError(result.error || t('socialPublish.publishError'));
      }

      // Reset status après 3s
      setTimeout(() => {
        setPublishingJobs(prev => ({ ...prev, [jobKey]: null }));
        setError('');
      }, 3000);

      await fetchStats();
    } catch (err) {
      console.error('Error triggering publish:', err);
      setPublishingJobs(prev => ({ ...prev, [jobKey]: 'error' }));
      setError(t('socialPublish.publishError'));
      setTimeout(() => setPublishingJobs(prev => ({ ...prev, [jobKey]: null })), 3000);
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const getToggleStatus = (codeInsee, network) => {
    const key = `${codeInsee}_${network}`;
    return socialConfigs[key]?.active || false;
  };

  const getConfigExists = (codeInsee, network) => {
    const key = `${codeInsee}_${network}`;
    const config = socialConfigs[key];
    return config?.page_id && config?.access_token;
  };

  if (adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bleu1 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('socialPublish.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title={t('socialPublish.title')} backPath="/settings" />

      <div className="p-4 max-w-4xl mx-auto">
        {/* Statistiques */}
        <div className="grid grid-cols-2 gap-3 mb-6 sm:grid-cols-4">
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-bleu1">{stats.totalActive}</p>
            <p className="text-xs text-gray-500">{t('socialPublish.stats.active')}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.totalPublished}</p>
            <p className="text-xs text-gray-500">{t('socialPublish.stats.published')}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{stats.totalPending}</p>
            <p className="text-xs text-gray-500">{t('socialPublish.stats.pending')}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{stats.totalFailed}</p>
            <p className="text-xs text-gray-500">{t('socialPublish.stats.failed')}</p>
          </div>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
            <button
              onClick={() => setError('')}
              className="float-right text-red-400 hover:text-red-600"
              aria-label={t('common.close')}
            >
              ✕
            </button>
          </div>
        )}

        {/* Tableau des communes */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('socialPublish.table.commune')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('socialPublish.table.insee')}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facebook
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('socialPublish.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bleu1 mx-auto"></div>
                    </td>
                  </tr>
                ) : communes.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      {t('socialPublish.noCommunes')}
                    </td>
                  </tr>
                ) : (
                  communes.map((commune) => {
                    const fbActive = getToggleStatus(commune.code_commune_insee, 'facebook');
                    const fbConfigured = getConfigExists(commune.code_commune_insee, 'facebook');
                    const fbJobKey = `${commune.code_commune_insee}_facebook`;
                    const fbJobStatus = publishingJobs[fbJobKey];

                    return (
                      <tr key={commune.code_commune_insee} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {commune.nom_de_la_commune}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {commune.code_commune_insee}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {/* Toggle switch */}
                            <button
                              onClick={() => toggleNetwork(commune.code_commune_insee, 'facebook', fbActive)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-bleu1 focus:ring-offset-2 ${
                                fbActive ? 'bg-bleu1' : 'bg-gray-300'
                              }`}
                              role="switch"
                              aria-checked={fbActive}
                              aria-label={`Facebook ${commune.nom_de_la_commune}`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  fbActive ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            {/* Indicateur de configuration */}
                            {fbActive && !fbConfigured && (
                              <span className="text-xs text-yellow-600">
                                {t('socialPublish.notConfigured')}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => triggerPublish(commune.code_commune_insee, 'facebook')}
                            disabled={!fbActive || !fbConfigured || fbJobStatus === 'loading'}
                            className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                              !fbActive || !fbConfigured
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : fbJobStatus === 'loading'
                                ? 'bg-bleu1/20 text-bleu1 cursor-wait'
                                : fbJobStatus === 'success'
                                ? 'bg-green-100 text-green-700'
                                : fbJobStatus === 'error' || fbJobStatus === 'no_event'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-bleu1 text-white hover:bg-bleu2'
                            }`}
                            aria-label={t('socialPublish.publishNow')}
                          >
                            {fbJobStatus === 'loading' ? (
                              <>
                                <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                                {t('socialPublish.publishing')}
                              </>
                            ) : fbJobStatus === 'success' ? (
                              <>✓ {t('socialPublish.published')}</>
                            ) : fbJobStatus === 'error' ? (
                              <>✕ {t('socialPublish.error')}</>
                            ) : fbJobStatus === 'no_event' ? (
                              t('socialPublish.noEvent')
                            ) : (
                              t('socialPublish.publishNow')
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                {t('socialPublish.page')} {currentPage} / {totalPages} ({totalCount} {t('socialPublish.communes')})
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm rounded-lg border border-gray-300 disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  aria-label={t('common.previous')}
                >
                  ←
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm rounded-lg border border-gray-300 disabled:opacity-50 hover:bg-gray-50 transition-colors"
                  aria-label={t('common.next')}
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Section historique des dernières publications */}
        <RecentJobs t={t} />
      </div>
    </div>
  );
};

/**
 * Composant interne pour afficher les dernières publications
 */
const RecentJobs = ({ t }) => {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  useEffect(() => {
    const fetchRecentJobs = async () => {
      try {
        const { data, error } = await supabase
          .from('social_publish_jobs')
          .select(`
            *,
            event:events(title)
          `)
          .order('created_at', { ascending: false })
          .limit(10);

        if (error) throw error;
        setJobs(data || []);
      } catch (err) {
        console.error('Error fetching recent jobs:', err);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchRecentJobs();
  }, []);

  const getStatusBadge = (status) => {
    const styles = {
      published: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="mt-6">
      <h2 className="text-h2 mb-3">{t('socialPublish.recentJobs')}</h2>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loadingJobs ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bleu1 mx-auto"></div>
          </div>
        ) : jobs.length === 0 ? (
          <p className="p-4 text-center text-gray-500">{t('socialPublish.noJobs')}</p>
        ) : (
          <div className="divide-y divide-gray-200">
            {jobs.map((job) => (
              <div key={job.id} className="p-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {job.event?.title || t('socialPublish.unknownEvent')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {job.code_commune_insee} · {job.network} · {new Date(job.created_at).toLocaleString('fr-FR')}
                    </p>
                  </div>
                  <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusBadge(job.status)}`}>
                    {t(`socialPublish.status.${job.status}`)}
                  </span>
                </div>
                {job.error_message && (
                  <p className="mt-1 text-xs text-red-600 truncate">{job.error_message}</p>
                )}
                {job.post_url && (
                  <a
                    href={job.post_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 text-xs text-bleu1 hover:underline inline-block"
                  >
                    {t('socialPublish.viewPost')}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialPublishPage;
